import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import Stepper from "../components/Nav/Stepper";
import TicketingDetails from "../components/Event/TicketingDetails";
import * as api from "../api";
import CheckOut from "../components/Event/Checkout";
import { options as dateOptions } from "../utils/dateFormat";
import { options as timeOptions } from "../utils/timeFormat";
import ExpiryPopup from "../components/Utils/ExpiryPopup";
import { ReactComponent as Cancel } from "../assets/images/cross.svg";
import Pay from "./../components/Event/Pay";
import GuestsInfo from "../components/Event/GuestsInfo";
import {
  external_platform,
  hardcoded_event,
  isMassTicketingReservationFlow,
} from "../constants";
import Loader from "../components/Utils/Loader";
import qs from "qs";
import { useGetUserCurrencyFromRegion } from "../utils/useGetUserCurrencyFromRegion";
import PaymentService from "../components/Payment/PaymentService";
import PreRegisterForm from "../components/Event/PreregistrationForm";
import EmptyPageWithMessage from "../components/Utils/EmptyPageWithMessage";
import OtpVerification from "../components/Auth/OtpVerification";
import Overlay from "../components/Utils/Overlay";
import { parseTierIncrement } from "../utils/ticketTier";
import { isTableTier, seatsForTables, stepForTier } from "../utils/table";
import {
  loadBookingDraft,
  persistBookingDraft,
  clearBookingDraft,
} from "../utils/bookingDraft";
import { pathWithSearch } from "../utils/navigation";
import { EVENTS, track } from "../utils/tracking";

const buildGuestsFromCart = (cartItems, loggedInUser) => {
  const built = [];
  for (const tierKey in cartItems) {
    const item = cartItems[tierKey];
    const qty = item.quantity || 0;
    for (let q = 0; q < qty; q++) {
      built.push({
        pk: loggedInUser.pk,
        ticket: {
          ticketTier: item.ticketTier,
        },
      });
    }
  }
  return built;
};

/**
 * Cart value for the pre-checkout tracking events.
 *
 * The `subtotal` state is only written by Checkout/GuestsInfo/Pay, none of which
 * have rendered while the visitor is still picking tickets — reading it there
 * always yields the initial 0. Same formula those components use
 * (sum of quantity x price); addons are deliberately excluded because they are
 * not selectable yet at this point in the funnel.
 */
const getSelectedSubtotal = (selection) => {
  let sum = 0;
  for (const key in selection || {}) {
    const item = selection[key];
    sum += (Number(item?.price) || 0) * (Number(item?.count) || 0);
  }
  return sum;
};

const BookingPage = ({
  user = {},
  showLogin = () => { },
  showRegister = () => { },
  setNewUser = () => { },
  handleLogin = () => { },
  setRegistrationHeader = () => { },
  history = {},
  match = {},
  handleRegistrationPopup = () => { },
  setActiveOrder = () => { },
  expiry = null,
  setExpiry = () => { },
  userLoading = true,
  isLoginVisible,
}) => {
  const [event, setEvent] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState([
    {
      label: "Passes",
      subtitle: "Select your passes",
      name: "tickets",
      visitable: false,
    },
    {
      label: "Checkout",
      subtitle: "Select your add-ons",
      name: "checkout",
      visitable: false,
    },
    {
      label: "Pay",
      subtitle: "Select your payment method",
      name: "pay",
      visitable: false,
    },
  ]);
  const [tickets, setTickets] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState({});
  const [selectedSlots, setSelectedSlots] = useState({});
  let token = useRef(null);
  let cartCode = useRef(null);
  let ticketToken = useRef(null);
  const [cart, setCart] = useState({});
  const [selectedAddons, setSelectedAddons] = useState({});
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ticketCount, setTicketCount] = useState(0);
  const [paymentData, setPaymentData] = useState({});
  const [paymentSubmitLoading, setPaymentSubmitLoading] = useState(false);
  const [paymentError, setPaymentError] = useState({});
  const [id] = useState(match.params.id);
  const [fetchError, setFetchError] = useState("");
  const [expired, setExpired] = useState(false);
  const [showRepalceOrderPopup, setShowReplaceOrderPopup] = useState(false);
  const [subtotal, setSubtotal] = useState(0);
  const [showReceipt, setShowReceipt] = useState(false);
  const [grandTotal, setGrandTotal] = useState(0);
  const [appliedVoucherCode, setAppliedVoucherCode] = useState("");
  const [voucherDiscount, setVoucherDiscount] = useState(0);
  const [voucherMessage, setVoucherMessage] = useState("");
  const [tempCart, setTempCart] = useState({});
  const [guests, setGuests] = useState([]);
  const [stepsLoaded, setStepsLoaded] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [nextAction, setNextAction] = useState({});
  const [showBookingOtp, setShowBookingOtp] = useState(false);
  const [bookingOtpEmail, setBookingOtpEmail] = useState("");
  const [bookingOtpInitialCooldown, setBookingOtpInitialCooldown] = useState(0);
  const [bookingOtpInitialError, setBookingOtpInitialError] = useState("");
  const [ticketsPool, setTicketsPool] = useState({});
  const [termsAndConditions, setTermsAndConditions] = useState({});
  const expiryTimeout = useRef(null);
  const [showContent, setShowContent] = useState(false);
  const [enforceLogin, setEnforceLogin] = useState(false);
  const [lockTickets, setLockTickets] = useState(false);
  const bookingBasePath = `/booking/${id}`;
  const { userCurrency, isLoading: userCurrencyLoading } =
    useGetUserCurrencyFromRegion();
  const isSubmitted = useRef(false);
  const restoredPreferredRegionRef = useRef(null);
  const [preferredRegion, setPreferredRegion] = useState({});
  const [slotConfirmTick, setSlotConfirmTick] = useState(0);
  const handleTicketsFetchLatestRef = useRef(null);
  const cartRef = useRef(cart);

  useEffect(() => {
    //redirect to another platform for this event
    if (match?.params?.id == external_platform?.id) {
      window.location.replace(external_platform.redirect_url);
    }
    if (hardcoded_event !== null && hardcoded_event != match?.params?.id) {
      history.replace(
        pathWithSearch(`/booking/${hardcoded_event}`, history.location.search)
      );
      window.location.reload();
    }
  }, [match]);

  //Cleanup for timer on page close
  useEffect(() => {
    return () => {
      if (expiryTimeout?.current) {
        setExpiry(null);
        clearInterval(expiryTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    ticketToken.current = qs.parse(history?.location?.search, {
      ignoreQueryPrefix: true,
    }).token;
    if (!userLoading && !userCurrencyLoading) {
      let fetchedEvent = {};
      let fetchedAddons = [];
      api.events.getEventById(id).then((response) => {
        if (
          response.status &&
          response.status >= 200 &&
          response.status < 300
        ) {
          let responseEvent = {
            title: response.data.name,
            description: response.data.description,
            image: response.data.thumbnail,
            date: new Date(response.data.startsAt).toLocaleDateString(
              [],
              dateOptions
            ),
            artists: response.data.artists,
            location: response.data.location,
            startsAt: new Date(response.data.startsAt).toLocaleTimeString(
              [],
              timeOptions
            ),
            startsAtRaw: response.data.startsAt,
            endsAtRaw: response.data.endsAt,
            id: response.data.pk,
            layoutImage: response.data.siteMap,
            type: response.data.reservationProcessType,
            reservationFlow: response.data.reservationFlow,
            isSeated: response?.data?.isSeated,
            eventKey: response?.data?.eventKey,
            regions: response?.data?.availableRegions?.map((region) => ({
              label: region?.name,
              value: region?.id,
              currency: region?.currency,
              skipMoto: region?.skipMoto,
              isDefault: region?.default,
              commission: region?.commissionRate,
            })),
            autoGenerateEmails: !response?.data?.requireEmailForTickets,
            allowMultipleTiers: response?.data?.allowMultipleTiersInBasket,
            maxTicketsPerCart: response?.data?.maxTicketsPerCart ?? null,
            autoApprove: response?.data?.autoApprove,
          };
          const defaultRegion = responseEvent?.regions?.find(
            (item) => item?.isDefault == true
          );
          const urlRegion = responseEvent?.regions?.find(
            (item) =>
              item?.label?.toLowerCase() ==
              qs
                .parse(history?.location?.search, {
                  ignoreQueryPrefix: true,
                })
                ?.region?.toLowerCase()
          );
          const singleRegion =
            responseEvent?.regions?.length === 1
              ? responseEvent.regions[0]
              : undefined;
          const selectedRegion = urlRegion
            ? urlRegion
            : singleRegion
              ? singleRegion
              : !response?.data?.autoSelectRegion
                ? defaultRegion
                : responseEvent?.regions?.find(
                  (item) => item?.currency == userCurrency
                ) || defaultRegion;
          const restoredRegion = restoredPreferredRegionRef.current;
          const resolvedRegion = restoredRegion
            ? responseEvent?.regions?.find(
              (item) => item?.value === restoredRegion?.value
            ) || selectedRegion
            : selectedRegion;
          setPreferredRegion({
            ...resolvedRegion,
          });
          setLockTickets(
            response?.data?.isInPreRegister && !Boolean(ticketToken?.current)
          );
          if (response.data?.terms) {
            setTermsAndConditions({
              title: response.data?.terms?.content || "",
              terms: response.data?.terms?.terms?.map((item) => {
                return { ...item, checked: false };
              }),
            });
          }
          fetchedEvent = responseEvent;
          setEvent(responseEvent);
          listTicketTiers(
            !response?.data?.isInPreRegister ||
            (response?.data?.isInPreRegister &&
              Boolean(ticketToken?.current)),
            resolvedRegion
          );
        }
        api.events
          .addons(id)
          .then((response) => {
            setAddons(() => {
              let arr = response.data.results.map((addon) => {
                return {
                  name: addon.name,
                  id: addon.id,
                  type: addon.addonType,
                  description: addon.description,
                  price: addon.price / 100,
                  collapsed: true,
                };
              });
              return arr;
            });
            fetchedAddons = response.data.results;
          })
          .finally(() => {
            handleDynamicSteps(fetchedEvent, fetchedAddons);
          });
      });
    }
    return () => {
      setRegistrationHeader({});
    };
  }, [userLoading, userCurrencyLoading]);

  const handleCurrencyChange = (currency) => {
    restoredPreferredRegionRef.current = null;
    setPreferredRegion(currency);
    setSelectedTickets([]);
    listTicketTiers(!lockTickets, currency);
  };

  const listTicketTiers = (canView = true, currency = "") => {
    if (canView) {
      setLoading(true);
      api.events
        .ticketTiers(id, ticketToken?.current, currency?.value)
        .then((response) => {
          setLoading(false);
          if (
            response.status &&
            response.status >= 200 &&
            response.status < 300
          ) {
            //if user has a token and still getting empty tiers, then token is invalid
            if (response.data.results.length === 0 && ticketToken?.current) {
              setLockTickets(true);
              return;
            }

            // Hardcoded sub-tier relationships until the API supports isSubTier/subTiers fields.
            // 536 is a sub-tier of 560; 537 is a sub-tier of 562.
            const SUB_TIER_MAP = { 536: [560, 579], 537: [562, 580] };
            const SUB_TIER_IDS = new Set([560, 562, 579, 580]);

            setTickets(() => {
              const returnedPks = new Set(response.data.results.map((t) => t.pk));

              let tickets = response.data.results.map((ticket) => {
                return {
                  name: ticket.name,
                  name2: ticket.nameLine2,
                  name3: ticket.nameLine3,
                  id: ticket.pk,
                  price: ticket.price / 100,
                  currency: ticket.region.currency,
                  state: getTicketState(ticket),
                  description: ticket.description,
                  category: ticket.category,
                  commissionRate: ticket?.region?.commissionRate,
                  hasSlots: ticket?.hasSlots || false,
                  maxDaysToShow: ticket?.maxDaysToShow ?? null,
                  expiresAt: ticket?.expiresAt ?? null,
                  minPrice: ticket?.minPrice != null ? ticket.minPrice / 100 : null,
                  maxPrice: ticket?.maxPrice != null ? ticket.maxPrice / 100 : null,
                  tierIncrement: ticket?.tierIncrement ?? null,
                  tableCapacity: ticket?.tableCapacity ?? null,
                  isSubTier: ticket?.isSubTier || SUB_TIER_IDS.has(ticket.pk),
                  subTiers: ticket?.subTiers?.length > 0
                    ? ticket.subTiers
                    : (SUB_TIER_MAP[ticket.pk] || []),
                };
              });
              return tickets;
            });
            track(EVENTS.VIEWED_BOOKING_PAGE, {
              eventId: id,
            });
            setShowContent(true);
          }
        })
        .catch((err) => {
          if (
            err?.response?.status == 401 &&
            !isMassTicketingReservationFlow(event)
          ) {
            showLogin(true);
            setEnforceLogin(true);
          }
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    if (
      Object.keys(user)?.length == 0 &&
      !userLoading &&
      enforceLogin &&
      !isLoginVisible
    ) {
      history.replace("/");
    } else if (
      Object.keys(user)?.length != 0 &&
      enforceLogin &&
      !isLoginVisible
    ) {
      listTicketTiers(
        !lockTickets || (lockTickets && ticketToken),
        preferredRegion
      );
    }
  }, [userLoading, enforceLogin, user, isLoginVisible, lockTickets]);

  const stepPath = (stepName) => {
    const code = cartCode.current;
    const stepSegment = stepName !== "tickets" ? `/${stepName}` : "";
    const codeSegment = code && stepName !== "tickets" ? `/${code}` : "";
    return `${bookingBasePath}${stepSegment}${codeSegment}${history.location.search || ""}`;
  };

  const goToBookingStep = (stepName) => {
    if (!steps.some((item) => item.name === stepName)) {
      return;
    }
    track(EVENTS.VIEWED_BOOKING_STEP, {
      step: stepName,
      eventId: id,
      cartCode: cartCode.current,
    });
    const path = stepPath(stepName);
    const pathnamePart = path.split("?")[0];
    if (history.location.pathname !== pathnamePart) {
      history.push(path);
    } else {
      const idx = steps.findIndex((item) => item.name === stepName);
      if (idx !== -1) {
        setActiveStep(idx);
      }
    }
  };

  const goToNextBookingStep = () => {
    const current = match.params.step;
    const ci =
      typeof current === "string"
        ? steps.findIndex((s) => s.name === current)
        : steps.findIndex((s) => s.name === "tickets");
    if (ci >= 0 && ci < steps.length - 1) {
      goToBookingStep(steps[ci + 1].name);
    }
  };

  const stepNamesSerialized = steps.map((s) => s.name).join(",");

  useEffect(() => {
    if (!stepsLoaded) return;

    // Restore cart code from URL segment if not already set (e.g. page refresh mid-flow)
    if (match.params.cartCode && !cartCode.current) {
      cartCode.current = match.params.cartCode;
    }

    const stepParam = match.params.step;
    if (!stepParam) {
      setActiveStep(0);
      return;
    }

    const validNames = steps.map((s) => s.name);
    if (!validNames.includes(stepParam)) {
      history.replace(stepPath("tickets"));
      return;
    }

    const needsCart = ["guests", "checkout", "pay"].includes(stepParam);
    if (needsCart && Object.keys(cart).length === 0) {
      history.replace(stepPath("tickets"));
      return;
    }

    if (stepParam === "guests" && event.type !== "full") {
      const fallback = steps.some((s) => s.name === "checkout")
        ? "checkout"
        : "pay";
      history.replace(stepPath(fallback));
      return;
    }

    const idx = steps.findIndex((s) => s.name === stepParam);
    if (idx !== -1) {
      setActiveStep(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL to step index only when route or wizard shape changes meaningfully
  }, [
    stepsLoaded,
    match.params.step,
    id,
    event?.type,
    Object.keys(cart).length,
    stepNamesSerialized,
  ]);

  useEffect(() => {
    let allSteps = [...steps];
    for (let i = 0; i < allSteps.length; i++) {
      if (allSteps[i].name === "checkout" || allSteps[i].name === "pay") {
        if (Object.keys(cart).length !== 0) {
          allSteps[i].visitable = true;
        } else {
          allSteps[i].visitable = false;
        }
      }
    }
    setSteps(allSteps);
  }, [activeStep, cart]);

  const getTicketState = (ticket) => {
    console.log("ticket", ticket);
    let now = new Date().getTime();
    if (
      ticket?.salesStartsAt &&
      new Date(ticket.salesStartsAt).getTime() > now && !(ticket.isInPreSales && ticket.isTokenAuthorized)
    ) {
      if (!ticket.showOnSaleTime) {
        return { label: "Coming Soon", type: "coming-soon" };
      } else {
        // Return the formatted date of when ticket tiers will be available
        return {
          label:
            "On Sale: " +
            new Date(ticket.salesStartsAt).toLocaleString("en-GB", {
              day: "numeric",
              month: "numeric",
              year: "numeric",
            }) +
            " " +
            new Date(ticket.salesStartsAt).toLocaleString("default", {
              hour: "numeric",
              minute: "numeric",
            }) +
            " ",
          type: "coming-soon",
        };
      }
    } else if (
      ticket?.salesEndsAt &&
      new Date(ticket.salesEndsAt).getTime() < now
    ) {
      return { label: "", type: "expired" };
    } else if (ticket.isSoldOut) {
      return { label: "Sold Out", type: "sold-out" };
    } else {
      return null;
    }
  };

  const nextStep = goToNextBookingStep;

  const handleStepClick = (name) => {
    let index = steps.findIndex((item) => item.name === name);
    if (index === -1) return;

    const needsExistingCart =
      name === "guests" || name === "checkout" || name === "pay";
    if (needsExistingCart && Object.keys(cart).length === 0) {
      return;
    }
    goToBookingStep(name);
  };

  const findTier = (ticketId) =>
    tickets.find((t) => t.id === ticketId || String(t.id) === String(ticketId));

  const getTierStep = (ticketId) => {
    if (event?.isSeated) return 1;
    const ticket = findTier(ticketId);
    // A table is ONE product: the cart counts tables, so "+" adds 1 however
    // many seats it contains. Seats appear only at the API boundaries below.
    return stepForTier(ticket, ticket?.tierIncrement);
  };

  const handleTicketClick = (id, name, price, type, seat = "", currency) => {
    const step = getTierStep(id);
    track(type === "add" ? EVENTS.ADDED_TICKET : EVENTS.REMOVED_TICKET, {
      tierId: id,
      tierName: name,
      price,
      currency,
      quantityDelta: step,
      eventId: match.params.id,
    });
    setSelectedTickets((current) => {
      let currentCopy = { ...current };
      if (type === "subtract" && currentCopy[id]) {
        if (currentCopy[id].count - step <= 0) {
          delete currentCopy[id];
        } else {
          let currentTicketCopy = { ...currentCopy[id] };
          currentTicketCopy.count = currentTicketCopy.count - step;
          if (event?.isSeated) {
            currentTicketCopy.seats = currentTicketCopy.seats.filter(
              (item) => item !== seat
            );
          }
          currentCopy[id] = currentTicketCopy;
        }
      } else if (type === "add" && currentCopy[id]) {
        let currentTicketCopy = { ...currentCopy[id] };
        currentTicketCopy.count = currentTicketCopy.count + step;
        if (event?.isSeated) {
          currentTicketCopy.seats = currentTicketCopy.seats.concat(seat);
        }
        currentCopy[id] = currentTicketCopy;
      } else if (type === "add" && !currentCopy[id]) {
        currentCopy[id] = { name, price, count: step, currency };
        if (event?.isSeated) {
          currentCopy[id].seats = [seat];
          currentCopy[id].count = 1;
        }
      }
      return currentCopy;
    });
  };

  const handleTicketsFetch = () => {
    if (Object.keys(selectedTickets).length === 0) {
      return;
    }
    const selectedSubtotal = getSelectedSubtotal(selectedTickets);
    track(EVENTS.CLICKED_CHECKOUT, {
      eventId: id,
      ticketCount,
      subtotal: selectedSubtotal,
      currency: preferredRegion?.currency,
    });
    if (
      Object.keys(user).length === 0 &&
      event.type === "full" &&
      !isMassTicketingReservationFlow(event)
    ) {
      track(EVENTS.LOGIN_WALL_SHOWN, {
        eventId: id,
        ticketCount,
        subtotal: selectedSubtotal,
        currency: preferredRegion?.currency,
      });
      showLogin(true);
      setNextAction({ action: "fetchTickets", payload: null });
      return;
    }
    setLoading(true);
    let items = [];
    // Maps the effective ticketTier sent to the API -> the selectedTickets key (parent tier id)
    const sentTierToSelectedKey = {};
    for (const tier in selectedTickets) {
      // Boundary 1: the cart API wants SEATS and validates them against
      // tier_increment, so a table of 10 is sent as quantity 10.
      let item = {
        ticketTier: tier,
        quantity: seatsForTables(findTier(tier), selectedTickets[tier].count),
      };
      if (event?.isSeated) {
        item.seats = selectedTickets[tier].seats;
      }
      const slotForTier = selectedSlots[tier] || selectedSlots[Number(tier)];
      if (slotForTier) {
        item.slot = slotForTier.id ?? slotForTier.pk;
        if (slotForTier._bookingTierId) {
          item.ticketTier = String(slotForTier._bookingTierId);
        }
      }
      sentTierToSelectedKey[item.ticketTier] = tier;
      items = items.concat(item);
    }
    setFetchError("");
    api.cart
      .addTicketsToCart(items, id, token.current)
      .then((response) => {
        if (
          response.status &&
          response.status >= 200 &&
          response.status < 300
        ) {
          setExpiry(null);
          token.current = response.data.token;
          cartCode.current = response.data.cart.code;
          const cartItems = response.data.cart.items ?? [];
          const cartExpiresAt = response.data.cart.expiresAt;
          setCart(() => {
            let obj = {};
            let items = cartItems;
            for (let i = 0; i < items.length; i++) {
              const selectedKey =
                sentTierToSelectedKey[items[i].ticketTier] ??
                sentTierToSelectedKey[String(items[i].ticketTier)] ??
                items[i].ticketTier;
              // Look up the actually-booked tier (may be a sub-tier) for name display.
              const bookedTier = tickets.find(
                (t) => String(t.id) === String(items[i].ticketTier)
              );
              const parentSelectedEntry = selectedTickets[selectedKey];
              let tier = {
                id: items[i].id,
                quantity: items[i].quantity,
                name: bookedTier?.name ?? parentSelectedEntry?.name,
                price: parentSelectedEntry?.price ?? bookedTier?.price,
                currency: parentSelectedEntry?.currency ?? bookedTier?.currency,
                ticketTier: items[i].ticketTier,
              };
              if (event?.isSeated) {
                tier.seats = items[i].seats;
              }
              const slotForTier =
                selectedSlots[selectedKey] || selectedSlots[Number(selectedKey)];
              if (slotForTier) {
                tier.slot = slotForTier;
              }
              obj[items[i].ticketTier] = tier;
            }
            return obj;
          });
          let newExpiry = new Date(cartExpiresAt).getTime();
          let now = new Date().getTime();
          /* addTimerToNavbar(expiry); */
          if (event.type === "full") {
            setActiveOrder(true);
          }
          setExpiry(newExpiry);
          setLoading(false);

          // Analytics: fire cart_created event with cart code and items
          window?.dataLayer?.push({
            event: "cart_created",
            cart: {
              code: cartCode.current,
              eventId: response.data.cart.event,
              items: cartItems,
              expiresAt: cartExpiresAt,
            },
          });

          track(EVENTS.STARTED_CHECKOUT, {
            cartCode: cartCode.current,
            eventId: id,
            ticketCount,
            subtotal: selectedSubtotal,
            currency: preferredRegion?.currency,
          });

          const ticketsIdx = steps.findIndex((s) => s.name === "tickets");
          const nextName =
            ticketsIdx >= 0 && ticketsIdx + 1 < steps.length
              ? steps[ticketsIdx + 1].name
              : null;
          if (nextName) {
            // Defer so cart state is committed before URL sync runs (avoids bounce to /tickets).
            setTimeout(() => goToBookingStep(nextName), 0);
          }
          setRegistrationHeader({
            now,
            expiry: newExpiry,
            eventTitle: event.title,
            ticketCount,
          });
          if (
            Object.keys(user).length === 0 &&
            !isMassTicketingReservationFlow(event)
          ) {
            showLogin(true);
          }
          startExpiryCountdown(newExpiry);
        }
      })
      .catch((error) => {
        setLoading(false);
        track(EVENTS.ADD_TO_CART_FAILED, {
          eventId: id,
          status: error?.response?.status,
          message: error?.response?.data?.nonFieldErrors?.[0],
        });
        if (
          error.response &&
          error.response.status &&
          error.response.status === 400
        ) {
          setFetchError(error.response.data.nonFieldErrors[0]);
        }
      });
  };

  const handleApplyVoucher = async (code) => {
    const response = await api.vouchers.validateVoucher(
      code,
      token.current,
      event.id
    );
    if (
      response.status &&
      response.status >= 200 &&
      response.status < 300
    ) {
      const data = response.data;
      if (!data?.valid) {
        throw new Error(data?.message || "Invalid voucher code");
      }
      setAppliedVoucherCode(data?.voucher?.code || code);
      setVoucherDiscount((data?.discountCents || 0) / 100);
      setVoucherMessage(data?.message || "");
      return;
    }
    throw new Error("Invalid voucher code");
  };

  const handleRemoveVoucher = () => {
    setAppliedVoucherCode("");
    setVoucherDiscount(0);
    setVoucherMessage("");
  };

  const handlePaymentSubmit = (paymentMethod, paymentGroup = null, email = null) => {
    setPaymentError({});
    if (expired) {
      return;
    }
    if (Object.keys(user).length === 0) {
      if (isMassTicketingReservationFlow(event) && email) {
        setNextAction({
          action: "pay",
          payload: { paymentMethod, paymentGroup },
        });
        api.auth
          .login(email)
          .then((response) => {
            if (response.status >= 200 && response.status < 300) {
              setBookingOtpEmail(response.data?.email || email);
              setBookingOtpInitialCooldown(60);
              setBookingOtpInitialError("");
              setShowBookingOtp(true);
            }
          })
          .catch((error) => {
            const errData = error.response?.data;
            if (errData?.code === "otp_throttled") {
              const retryAfter = parseInt(errData.retryAfter, 10) || 60;
              setBookingOtpEmail(email);
              setBookingOtpInitialCooldown(retryAfter);
              setBookingOtpInitialError(
                errData.detail ||
                "Too many OTP requests. Please try again later."
              );
              setShowBookingOtp(true);
            } else if (error.response?.status === 404) {
              setNewUser({ email });
              showRegister(true);
            } else {
              setPaymentError({
                message:
                  errData?.detail ||
                  errData?.nonFieldErrors?.[0] ||
                  "Something went wrong. Please try again.",
                paymentMethod,
                paymentGroup,
              });
            }
          });
        return;
      }
      showLogin(true);
      let payload = {
        paymentMethod,
        paymentGroup,
      };
      setNextAction({ action: "pay", payload });
      return;
    } else {
      isSubmitted.current = true;
      setPaymentSubmitLoading(true);
      let addonItems = [];
      for (const item in selectedAddons) {
        addonItems = addonItems.concat({
          quantity: selectedAddons[item].count,
          addon: item,
        });
      }
      if (event.type === "full") {
        let items = [];
        let reservationHead = user.pk;
        let guestsList = guests;
        if (isMassTicketingReservationFlow(event) && guests.length === 0) {
          const cartSource =
            Object.keys(tempCart).length > 0 ? tempCart : cart;
          guestsList = buildGuestsFromCart(cartSource, user);
        }
        // A table is a package: the order must carry ALL its seats as items even
        // if the buyer named only one guest. Named rows get their customer; the
        // remaining seats are sent with none, and the backend creates the
        // placeholders that the host replaces on the roster screen.
        const namedPerTier = {};
        for (const g of guestsList) {
          const tierId = g?.ticket?.ticketTier ?? g?.ticket?.value;
          if (tierId == null) continue;
          namedPerTier[String(tierId)] = (namedPerTier[String(tierId)] || 0) + 1;
        }
        const cartSeats = Object.keys(tempCart).length > 0 ? tempCart : cart;
        const unnamedItems = [];
        for (const tierId in cartSeats) {
          if (!isTableTier(findTier(tierId))) continue;
          const seats = cartSeats[tierId]?.quantity ?? 0;
          const named = namedPerTier[String(tierId)] || 0;
          for (let s = 0; s < Math.max(0, seats - named); s++) {
            unnamedItems.push({ ticketTier: tierId });
          }
        }

        for (let i = 0; i < guestsList.length; i++) {
          // A blank table seat has no registered user, so send the item WITHOUT
          // a customer: _ensure_item_customers then creates the placeholder and
          // the host names the guest on the roster screen later. Every other
          // flow always has a pk here, so this changes nothing for them.
          let item = {
            ticketTier: guestsList[i].ticket.ticketTier,
          };
          if (guestsList[i].pk) {
            item.customer = guestsList[i].pk;
          }
          const tierKey = guestsList[i].ticket.ticketTier;
          const slotForGuest =
            selectedSlots[tierKey] ||
            selectedSlots[Number(tierKey)] ||
            Object.values(selectedSlots).find(
              (s) => String(s._bookingTierId) === String(tierKey)
            );
          if (slotForGuest) {
            item.slot = slotForGuest.id ?? slotForGuest.pk;
          }
          items.push(item);
        }
        items = items.concat(unnamedItems);
        api.reservations
          .submitReservation(
            reservationHead,
            items,
            addonItems,
            event.id,
            preferredRegion?.skipMoto ? "card" : paymentMethod,
            paymentGroup === "pwf" ? true : false,
            token.current,
            ticketToken.current,
            appliedVoucherCode || null
          )
          .then((response) => {
            if (
              response.status &&
              response.status >= 200 &&
              response.status < 300
            ) {
              const hasPaymentData =
                response?.data?.cardPaymentUrl ||
                response?.data?.extraData?.clientSecret;
              track(EVENTS.INITIATED_PAYMENT, {
                orderId: response?.data?.order,
                eventId: id,
                grandTotal,
                currency: preferredRegion?.currency,
                paymentSolution: response?.data?.paymentSolution,
                paymentMethod,
                isSetupIntent: String(
                  response?.data?.extraData?.clientSecret || ""
                ).startsWith("seti_"),
              });
              if (hasPaymentData && paymentMethod !== "valu") {
                setPaymentData({
                  type: paymentMethod,
                  url: response.data.cardPaymentUrl,
                  reference: response.data.billReference,
                  paymentSolution: response?.data?.paymentSolution,
                  orderId: response.data.order,
                  clientSecret: response?.data?.extraData?.clientSecret,
                });
              } else {
                clearBookingDraft(id);
                history.push(`/orders/${response.data.order}${history.location.search}`);
              }
              if (paymentMethod === "kiosk") {
                clearBookingDraft(id);
                history.replace(`/orders/${response.data.order}${history.location.search}`);
              }
            } else {
              setPaymentSubmitLoading(false);
            }
          })
          .catch((error) => {
            isSubmitted.current = false;
            if (
              error?.response?.status === 400 &&
              error?.response?.data?.nonFieldErrors
            ) {
              setPaymentError({
                message: error.response.data.nonFieldErrors[0],
                paymentMethod,
                paymentGroup,
                action: handleStepClick,
              });
            } else {
              setPaymentError({
                message: "Something went wrong while processing your order",
                paymentMethod,
                paymentGroup,
              });
            }
            setPaymentSubmitLoading(false);
          });
      } else {
        api.cart
          .payCart(addonItems, token.current, paymentMethod)
          .then((response) => {
            if (
              response.status &&
              response.status >= 200 &&
              response.status < 300
            ) {
              setPaymentData({
                type: paymentMethod,
                url: response.data.cardPaymentUrl,
                reference: response.data.billReference,
                paymentSolution: response?.data?.paymentSolution,
                orderId: response.data.order,
                clientSecret: response?.data?.extraData?.clientSecret,
              });
              if (paymentMethod === "kiosk") {
                clearBookingDraft(id);
                history.replace(`/orders/${response.data.order}${history.location.search}`);
              }
            } else {
              setPaymentSubmitLoading(false);
            }
          })
          .catch(() => {
            setPaymentError({
              message: "Something went wrong while processing your order",
              paymentMethod,
              paymentGroup,
            });
            setPaymentSubmitLoading(false);
          });
      }
    }
  };

  const handleExpiryRetry = () => {
    history.replace(stepPath("tickets"));
    setExpired(false);
  };

  const handleReplaceOrder = () => {
    setShowReplaceOrderPopup(false);
    handleTicketsFetch();
  };

  const cancelCart = () => {
    clearBookingDraft(id);
    restoredPreferredRegionRef.current = null;
    if (token.current) {
      api.cart.cancelCart(token.current);
    }
    setActiveOrder(false);
    setExpiry(null);
    handleRegistrationPopup("login", false);
    handleRegistrationPopup("register", false);
    handleRegistrationPopup("forgotPassword", false);
    setPaymentData({});
    setRegistrationHeader({});
    setShowReplaceOrderPopup(false);
    setCart({});
    setTicketsPool({});
    setGuests([]);
    setTempCart({});
    setSelectedTickets({});
    setSelectedSlots({});
    if (expiryTimeout?.current) {
      clearInterval(expiryTimeout.current);
    }
    token.current = null;
    cartCode.current = null;
    history.replace(stepPath("tickets"));
    setShowCancelConfirmation(false);
  };

  // Keep refs in sync so the slotConfirmTick effect always reads fresh values.
  handleTicketsFetchLatestRef.current = handleTicketsFetch;
  cartRef.current = cart;

  useEffect(() => {
    if (slotConfirmTick === 0) return;
    if (Object.keys(cartRef.current).length === 0) {
      handleTicketsFetchLatestRef.current?.();
    } else {
      setShowReplaceOrderPopup(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotConfirmTick]);

  const startExpiryCountdown = (newExpiryTs) => {
    if (expiryTimeout?.current) {
      clearInterval(expiryTimeout.current);
    }
    expiryTimeout.current = setInterval(() => {
      if (newExpiryTs - Date.now() <= 0) {
        track(EVENTS.CART_EXPIRED, {
          eventId: id,
          cartCode: cartCode.current,
          ticketCount,
        });
        setExpired(true);
        cancelCart();
      }
    }, 1000);
  };

  useLayoutEffect(() => {
    if (!id) return;
    const draft = loadBookingDraft(id);
    if (!draft?.token || !draft.cart || typeof draft.expiry !== "number") {
      return;
    }
    if (draft.expiry <= Date.now()) {
      clearBookingDraft(id);
      return;
    }
    token.current = draft.token;
    cartCode.current = draft.cartCode || null;
    setCart(draft.cart);
    setExpiry(draft.expiry);
    setSelectedTickets(draft.selectedTickets || {});
    setSelectedSlots(draft.selectedSlots || {});
    setSelectedAddons(draft.selectedAddons || {});
    setGuests(draft.guests || []);
    setTempCart(draft.tempCart || {});
    setTicketsPool(draft.ticketsPool || {});
    if (draft.preferredRegion?.value) {
      restoredPreferredRegionRef.current = draft.preferredRegion;
      setPreferredRegion(draft.preferredRegion);
    }
    if (draft.reservationFull) {
      setActiveOrder(true);
    }
    startExpiryCountdown(draft.expiry);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draft restore once per event id
  }, [id]);

  useEffect(() => {
    if (!event?.id) return;
    if (!token.current || Object.keys(cart).length === 0 || !expiry) {
      return;
    }
    if (expiry <= Date.now()) {
      return;
    }
    persistBookingDraft(id, {
      token: token.current,
      cartCode: cartCode.current,
      cart,
      expiry,
      reservationFull: event.type === "full",
      selectedTickets,
      selectedSlots,
      selectedAddons,
      guests,
      tempCart,
      ticketsPool,
      preferredRegion,
    });
  }, [
    cart,
    expiry,
    id,
    event.type,
    selectedTickets,
    selectedSlots,
    selectedAddons,
    guests,
    tempCart,
    ticketsPool,
    preferredRegion,
  ]);

  useEffect(() => {
    if (
      Object.keys(user).length === 0 &&
      event.type === "full" &&
      !isMassTicketingReservationFlow(event) &&
      match.params.step &&
      match.params.step !== "tickets"
    ) {
      cancelCart();
    } else if (Object.keys(user).length !== 0) {
      if (Object.keys(nextAction).length !== 0) {
        let action = { ...nextAction };
        let payload = action.payload !== null ? { ...action.payload } : null;
        setNextAction({});
        handleNextAction(action.action, payload);
      }
    }
  }, [user, nextAction]);

  const handleNextAction = (action, payload = null) => {
    switch (action) {
      case "fetchTickets":
        handleTicketsFetch();
        break;
      case "pay":
        handlePaymentSubmit(payload.paymentMethod, payload.paymentGroup);
        break;
      default:
        break;
    }
  };

  const handleDynamicSteps = (fetchedEvent, fetchedAddons) => {
    let stepsCopy = [...steps];
    if (
      fetchedEvent.type === "full" &&
      !isMassTicketingReservationFlow(fetchedEvent)
    ) {
      let check = steps.filter((step) => step.name === "guests");
      if (check.length === 0) {
        let step = {
          label: "Guests' Info",
          subtitle: "Enter your group information",
          name: "guests",
          visitable: true,
        };
        stepsCopy.splice(1, 0, step);
      }
    }
    if (fetchedAddons.length === 0) {
      let index = stepsCopy.findIndex((item) => item.name === "checkout");
      if (index !== -1) {
        stepsCopy.splice(index, 1);
      }
    }
    setSteps(stepsCopy);
    setStepsLoaded(true);
  };

  if (Object.keys(event)?.length == 0) {
    return (
      <div className="page-loader-container">
        <Loader />
      </div>
    );
  }

  if (lockTickets && event?.id) {
    /*return <div className="popup"><PreRegisterForm event={event} tier={event.title}/></div>;*/

    return (
      <EmptyPageWithMessage
        message={`Sandbox Festival 2026 Full Festival Passes Drop 2 is now live for those who pre-registered!  For everyone else please come back at 2pm EET to catch our special Flash Rate!`}
      />
    );
  }

  if (!stepsLoaded) {
    return (
      <div className="page-loader-container">
        <Loader />
      </div>
    );
  }

  return (
    <React.Fragment>
      {showContent && (
        <div
          className="event-page"
          onClick={(e) => {
            if (showReceipt) {
              setShowReceipt(false);
            }
          }}
        >
          {/*
          <div className="mobile-header">
            <div className="mobile-header__title">
              <h3 className="mobile-header__title-text">
                {steps[activeStep].label}
              </h3>
              {Object.keys(cart).length !== 0 && (
                <div
                  className="mobile-header__cancel"
                  onClick={() => setShowCancelConfirmation(true)}
                >
                  <Cancel className="mobile-header__cancel-icon" />
                </div>
              )}
            </div>
            <p className="mobile-header__subtitle">
              {steps[activeStep].subtitle}
            </p>
          </div>
          */}
          {stepsLoaded && false && (
            <Stepper
              steps={steps}
              active={activeStep}
              handleStepClick={/* handleStepClick */ () => { }}
            />
          )}
          {steps[activeStep]?.name === "tickets" && (
            <TicketingDetails
              tickets={tickets}
              setTickets={setTickets}
              layoutImage={event.layoutImage}
              selectedTickets={selectedTickets}
              setSelectedTickets={setSelectedTickets}
              cart={cart}
              handleTicketClick={handleTicketClick}
              handleTicketsFetch={() => {
                if (Object.keys(cart).length === 0) {
                  handleTicketsFetch();
                } else {
                  setShowReplaceOrderPopup(true);
                }
              }}
              handleRetry={handleTicketsFetch}
              loading={loading}
              ticketCount={ticketCount}
              setTicketCount={setTicketCount}
              fetchError={fetchError}
              setFetchError={setFetchError}
              expiry={expiry}
              event={event}
              nextStep={nextStep}
              selectedCurrency={preferredRegion}
              handleCurrencyChange={handleCurrencyChange}
              selectedSlots={selectedSlots}
              setSelectedSlots={setSelectedSlots}
              onSlotConfirmed={() => setSlotConfirmTick((prev) => prev + 1)}
            />
          )}
          {steps[activeStep]?.name === "checkout" && (
            <CheckOut
              user={user}
              id={id}
              event={event}
              tickets={cart}
              expiry={expiry}
              selectedAddons={selectedAddons}
              setSelectedAddons={setSelectedAddons}
              subtotal={subtotal}
              setSubtotal={setSubtotal}
              showReceipt={showReceipt}
              setShowReceipt={setShowReceipt}
              grandTotal={grandTotal}
              setGrandTotal={setGrandTotal}
              nextStep={nextStep}
              cancelOrder={() => setShowCancelConfirmation(true)}
              tempCart={tempCart}
              addons={addons}
              setAddons={setAddons}
              editGuests={guests.length > 0}
              handleStepClick={handleStepClick}
              currency={preferredRegion?.currency}
              commission={preferredRegion?.commission}
            />
          )}
          {steps[activeStep]?.name === "pay" && (
            <Pay
              user={user}
              event={event}
              tickets={cart}
              selectedSlots={selectedSlots}
              expiry={expiry}
              selectedAddons={selectedAddons}
              subtotal={subtotal}
              setSubtotal={setSubtotal}
              showReceipt={showReceipt}
              setShowReceipt={setShowReceipt}
              grandTotal={grandTotal}
              setGrandTotal={setGrandTotal}
              handlePaymentSubmit={handlePaymentSubmit}
              handleStepClick={handleStepClick}
              cancelOrder={() => setShowCancelConfirmation(true)}
              tempCart={tempCart}
              showEditAddons={addons.length > 0}
              editGuests={guests.length > 0}
              termsAndConditions={termsAndConditions}
              setTermsAndConditions={setTermsAndConditions}
              currency={preferredRegion?.currency}
              skipPayment={preferredRegion?.skipMoto && !event?.autoApprove}
              commission={preferredRegion?.commission}
              onApplyVoucher={handleApplyVoucher}
              onRemoveVoucher={handleRemoveVoucher}
              appliedVoucherCode={appliedVoucherCode}
              voucherDiscount={voucherDiscount}
              voucherMessage={voucherMessage}
            />
          )}

          {steps[activeStep]?.name === "guests" && (
            <GuestsInfo
              user={user}
              event={event}
              tickets={cart}
              tierList={tickets}
              expiry={expiry}
              selectedAddons={selectedAddons}
              subtotal={subtotal}
              setSubtotal={setSubtotal}
              showReceipt={showReceipt}
              setShowReceipt={setShowReceipt}
              grandTotal={grandTotal}
              setGrandTotal={setGrandTotal}
              nextStep={nextStep}
              jumpToStep={handleStepClick}
              newCart={tempCart}
              setNewCart={setTempCart}
              guestsMapper={guests}
              setGuestsMapper={setGuests}
              cancelOrder={() => setShowCancelConfirmation(true)}
              ticketsPool={ticketsPool}
              setTicketsPool={setTicketsPool}
              currency={preferredRegion?.currency}
              commission={preferredRegion?.commission}
            />
          )}

          <PaymentService
            type={paymentData?.paymentSolution}
            onClose={() => {
              isSubmitted.current = false;
              setPaymentSubmitLoading(false);
              setPaymentData({});
            }}
            onPaymentComplete={() => clearBookingDraft(id)}
            setLoading={setPaymentSubmitLoading}
            stripeClientSecret={paymentData?.clientSecret}
            isStripeSetup={paymentData?.clientSecret?.startsWith("seti_")}
            stripeAmountLabel={
              grandTotal ? `${grandTotal} ${preferredRegion?.currency || ""}`.trim() : ""
            }
            stripeRedirectUrl={`/payment-confirmation?merchant_order_id=${paymentData?.orderId}&solution=stripe${history.location.search ? '&' + history.location.search.substring(1) : ''}`}
            cardPaymentUrl={paymentData?.url}
            loading={paymentSubmitLoading}
            loadingMessage={
              !preferredRegion?.skipMoto || event.autoApprove
                ? "we're getting your payment form ready"
                : ""
            }
          />

          {showBookingOtp && (
            <React.Fragment>
              <OtpVerification
                email={bookingOtpEmail}
                close={() => setShowBookingOtp(false)}
                initialCooldown={bookingOtpInitialCooldown}
                initialError={bookingOtpInitialError}
                onVerified={(responseData) => {
                  handleLogin(
                    responseData.firstName,
                    responseData.lastName,
                    responseData.gender,
                    responseData.email,
                    responseData.mobileNumber,
                    responseData.pk
                  );
                  setShowBookingOtp(false);
                }}
              />
              <Overlay
                darkTheme={true}
                handleOnClick={() => setShowBookingOtp(false)}
              />
            </React.Fragment>
          )}

          {expired && (
            <ExpiryPopup
              title="Timer expired"
              message="Looks like your timer has expired"
              actionMessage="Do you wish to try again?"
              handleBtn1Click={handleExpiryRetry}
              btn1="Try Again"
              closable={false}
            />
          )}
          {showRepalceOrderPopup && (
            <ExpiryPopup
              title="Complete standing order"
              message="You currently have a standing purchase, by making a new order you will lose your fetched tickets."
              actionMessage="Are you sure you want to override your order? "
              handleBtn1Click={() => setShowReplaceOrderPopup(false)}
              handleBtn2Click={handleReplaceOrder}
              btn1="No, Complete standing order"
              btn2="Yes, override standing order"
              closable={true}
              close={() => setShowReplaceOrderPopup(false)}
              expiry={expiry}
            />
          )}
          {showCancelConfirmation && (
            <ExpiryPopup
              title="Timer"
              message="By cancelling your order you will lose your fetched passes."
              actionMessage="Are you sure you want to cancel your order? "
              handleBtn1Click={() => setShowCancelConfirmation(false)}
              handleBtn2Click={cancelCart}
              btn1="No, Dont't cancel my order"
              btn2="Yes, Cancel my order"
              closable={true}
              close={() => setShowCancelConfirmation(false)}
              expiry={expiry}
            />
          )}
          {Object.keys(paymentError).length !== 0 && (
            <ExpiryPopup
              title="Timer"
              variant="error"
              message={paymentError.message}
              handleBtn1Click={
                paymentError.action
                  ? () => {
                    paymentError.action("guests");
                    setPaymentError({});
                  }
                  : ""
              }
              handleBtn2Click={() => setPaymentError({})}
              btn1={paymentError.action ? "Edit Guests" : ""}
              btn2="Close"
              closable={true}
              close={() => setPaymentError({})}
              expiry={expiry}
            />
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default BookingPage;
